import React, {
  useImperativeHandle,
  useRef,
  useEffect,
  forwardRef,
  useState,
} from "react";
import { Modal } from "bootstrap";
import { BACKOFFICE_BASE_URL } from "../../services/TapApiUrls";
import HttpAPICall from "../../services/HttpAPICall";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Loader from "../../components/ui/Loader/Loader";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

const SubModuleUpdatePopup = forwardRef(({ onUpdateSuccess }, ref) => {
  const access_token = useSelector((state) => state.auth.access_token);
  const modalElem = useRef(null);
  const searchModal = useRef(null);
  const [formLoading, setFormLoading] = useState(false);

  const [formData, setFormData] = useState({
    sub_module_name: "",
    sub_module_id: null,
    module_id: null,
    tap_master_id: null,
    from_date: null,
    till_date: null,
  });

  const [isMultipleMode, setIsMultipleMode] = useState(false);
  const [selectedSubModulesList, setSelectedSubModulesList] = useState([]);
  const [selectedSubModuleIds, setSelectedSubModuleIds] = useState([]);
  const [multipleModuleId, setMultipleModuleId] = useState(null);
  const [multipleTapMasterId, setMultipleTapMasterId] = useState(null);
  const [multipleFromDate, setMultipleFromDate] = useState(null);
  const [multipleTillDate, setMultipleTillDate] = useState(null);

  useImperativeHandle(ref, () => ({
    openEditModal: (subModuleData ) => {
      if (Array.isArray(subModuleData) && subModuleData.length > 1) {
        setIsMultipleMode(true);
        setSelectedSubModulesList(subModuleData);
        setSelectedSubModuleIds(subModuleData.map(item => item.id));
        setMultipleModuleId(subModuleData[0]?.module_id || null);
        setMultipleTapMasterId(subModuleData[0]?.tap_master_id || null);
        setMultipleFromDate(null);
        setMultipleTillDate(null);
        searchModal.current.show();
      }
      else if (Array.isArray(subModuleData) && subModuleData.length === 1) {
        const singleData = subModuleData[0];
        setIsMultipleMode(false);
        setFormData({
          sub_module_name: singleData.name || "",
          sub_module_id: singleData.id || null,
          module_id: singleData.module_id || null,
          tap_master_id: singleData.tap_master_id || null,
          from_date: singleData.from ? moment(singleData.from).toDate() : null,
          till_date: singleData.till ? moment(singleData.till).toDate() : null,
        });
        searchModal.current.show();
      }
      else if (subModuleData && typeof subModuleData === 'object' && !Array.isArray(subModuleData)) {
        setIsMultipleMode(false);
        setFormData({
          sub_module_name: subModuleData.name || "",
          sub_module_id: subModuleData.id || null,
          module_id: subModuleData.module_id || null,
          tap_master_id: subModuleData.tap_master_id || null,
          from_date: subModuleData.from ? moment(subModuleData.from).toDate() : null,
          till_date: subModuleData.till ? moment(subModuleData.till).toDate() : null,
        });
        searchModal.current.show();
      } else {
        toast.error("Invalid data format", {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    },
    closeModal: () => {
      searchModalClose();
    },
  }));

  useEffect(() => {
    modalElem.current = document.getElementById("subModuleEditModalID");
    searchModal.current = new Modal(modalElem.current, {
      keyboard: false,
      backdrop: "static",
    });

    const handleHidden = () => {
      setFormLoading(false);
      setIsMultipleMode(false);
      setSelectedSubModulesList([]);
      setSelectedSubModuleIds([]);
      setMultipleFromDate(null);
      setMultipleTillDate(null);
    };

    modalElem.current.addEventListener("hidden.bs.modal", handleHidden);

    return () => {
      if (searchModal.current) {
        searchModal.current.dispose();
      }
      if (modalElem.current) {
        modalElem.current.removeEventListener("hidden.bs.modal", handleHidden);
      }
    };
  }, []);

  const searchModalClose = () => {
    if (searchModal.current) {
      searchModal.current.hide();
    }
    setFormLoading(false);
    setIsMultipleMode(false);
    setSelectedSubModulesList([]);
    setSelectedSubModuleIds([]);
    setMultipleFromDate(null);
    setMultipleTillDate(null);
  };

  const handleDateChange = (date, fieldName) => {
    if (isMultipleMode) {
      if (fieldName === 'from_date') {
        setMultipleFromDate(date);
      } else if (fieldName === 'till_date') {
        setMultipleTillDate(date);
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [fieldName]: date,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (isMultipleMode) {
      handleMultipleSubModuleUpdate();
    } else {
      handleSingleSubModuleUpdate();
    }
  };

  const handleSingleSubModuleUpdate = () => {
    if (!formData.from_date) {
      toast.error("From date is required", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    if (!formData.till_date) {
      toast.error("Till date is required", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    const fromDate = new Date(formData.from_date);
    const tillDate = new Date(formData.till_date);

    if (tillDate <= fromDate) {
      toast.error("Till date must be after From date", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    setFormLoading(true);

    const payload = {
      tap_master_id: formData.tap_master_id,
      module_id: formData.module_id,
      sub_modules: [formData.sub_module_id],
      form_date: moment(formData.from_date).format('YYYY-MM-DD'),
      till_date: moment(formData.till_date).format('YYYY-MM-DD'),
    };

    HttpAPICall.withAthorization(
      "PUT",
      BACKOFFICE_BASE_URL + "/group/update_sub_module",
      access_token,
      {},
      payload,
      (resp) => {
        toast.success("Sub-module updated successfully", {
          position: toast.POSITION.TOP_RIGHT,
        });

        if (onUpdateSuccess) {
          onUpdateSuccess();
        }

        searchModalClose();
      },
      (error) => {
        toast.error(error?.message || "Failed to update sub-module", {
          position: toast.POSITION.TOP_RIGHT,
        });
        console.error("Sub-module update error:", error);
      }
    ).finally(() => setFormLoading(false));
  };

  const handleMultipleSubModuleUpdate = () => {
    if (selectedSubModuleIds.length === 0) {
      toast.warning("Please select at least one sub-module", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    if (!multipleFromDate) {
      toast.error("From date is required", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    if (!multipleTillDate) {
      toast.error("Till date is required", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    const fromDate = new Date(multipleFromDate);
    const tillDate = new Date(multipleTillDate);

    if (tillDate <= fromDate) {
      toast.error("Till date must be after From date", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    setFormLoading(true);

    const payload = {
      tap_master_id: multipleTapMasterId,
      module_id: multipleModuleId,
      sub_modules: selectedSubModuleIds,
      form_date: moment(multipleFromDate).format('YYYY-MM-DD'),
      till_date: moment(multipleTillDate).format('YYYY-MM-DD'),
    };

    HttpAPICall.withAthorization(
      "PUT",
      BACKOFFICE_BASE_URL + "/group/update_sub_module",
      access_token,
      {},
      payload,
      (resp) => {
        toast.success(`${selectedSubModuleIds.length} sub-modules updated successfully`, {
          position: toast.POSITION.TOP_RIGHT,
        });

        if (onUpdateSuccess) {
          onUpdateSuccess();
        }

        searchModalClose();
      },
      (error) => {
        toast.error(error?.message || "Failed to update sub-modules", {
          position: toast.POSITION.TOP_RIGHT,
        });
        console.error("Multiple sub-modules update error:", error);
      }
    ).finally(() => setFormLoading(false));
  };

  const getSelectedSubModuleNames = () => {
    const names = selectedSubModulesList.map(item => item.name);

    if (names.length <= 2) {
      return names.join(', ');
    }

    return `${names.slice(0, 2).join(', ')} + ${names.length - 2} more`;
  };

  return (
    <>
      <div
        className="modal fade"
        id="subModuleEditModalID"
        tabIndex="-1"
        ref={modalElem}
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {isMultipleMode ? 'Multiple Sub-Module Update' : 'Edit Sub-Module'}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={searchModalClose}
                aria-label="Close"
                disabled={formLoading}
              />
            </div>

            <div className="modal-body">
              {formLoading ? (
                <div className="text-center py-5">
                  <Loader />
                </div>
              ) : (
                <form id="subModuleEditForm" onSubmit={handleSubmit}>
                  {isMultipleMode ? (
                    // Multiple sub-modules view - simplified with just selected names
                    <>
                      <div className="row">
                        <div className="row align-items-center py-2 justify-content-center">
                          <label className="col-sm-3 form-label require">Selected Sub-Modules</label>
                          <div className="col-sm-8">
                            <input
                              type="text"
                              className="form-control"
                              value={getSelectedSubModuleNames()}
                              disabled
                              style={{ backgroundColor: '#e9ecef' }}
                              placeholder="No sub-modules selected"
                            />
                          </div>
                        </div>

                        <div className="row align-items-center py-2 justify-content-center">
                          <label className="col-sm-3 form-label require">From Date</label>
                          <div className="col-sm-8">
                            <DatePicker
                              selected={multipleFromDate}
                              name="from_date"
                              onChange={(date) => handleDateChange(date, 'from_date')}
                              dateFormat="dd-MMM-yyyy"
                              className="form-control"
                              showMonthDropdown
                              showYearDropdown
                              autoComplete="off"
                              scrollMonthYearDropdown
                              placeholderText="Select From Date"
                              required
                            />
                          </div>
                        </div>

                        <div className="row align-items-center py-2 justify-content-center">
                          <label className="col-sm-3 form-label require">Till Date</label>
                          <div className="col-sm-8">
                            <DatePicker
                              selected={multipleTillDate}
                              name="till_date"
                              onChange={(date) => handleDateChange(date, 'till_date')}
                              dateFormat="dd-MMM-yyyy"
                              className="form-control"
                              showMonthDropdown
                              showYearDropdown
                              autoComplete="off"
                              scrollMonthYearDropdown
                              placeholderText="Select Till Date"
                              minDate={multipleFromDate || null}
                              required
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    // Single sub-module view
                    <>
                      <div className="row">
                        <div className="row align-items-center py-2 justify-content-center">
                          <label className="col-sm-3 form-label require">Sub-Module Name</label>
                          <div className="col-sm-8">
                            <input
                              type="text"
                              className="form-control"
                              name="sub_module_name"
                              value={formData.sub_module_name}
                              disabled
                              style={{ backgroundColor: '#e9ecef' }}
                            />
                          </div>
                        </div>

                        <div className="row align-items-center py-2 justify-content-center">
                          <label className="col-sm-3 form-label require">From Date</label>
                          <div className="col-sm-8">
                            <DatePicker
                              selected={formData.from_date}
                              name="from_date"
                              onChange={(date) => handleDateChange(date, 'from_date')}
                              dateFormat="dd-MMM-yyyy"
                              className="form-control"
                              showMonthDropdown
                              showYearDropdown
                              autoComplete="off"
                              scrollMonthYearDropdown
                              placeholderText="Select From Date"
                              required
                            />
                          </div>
                        </div>

                        <div className="row align-items-center py-2 justify-content-center">
                          <label className="col-sm-3 form-label require">Till Date</label>
                          <div className="col-sm-8">
                            <DatePicker
                              selected={formData.till_date}
                              name="till_date"
                              onChange={(date) => handleDateChange(date, 'till_date')}
                              dateFormat="dd-MMM-yyyy"
                              className="form-control"
                              showMonthDropdown
                              showYearDropdown
                              autoComplete="off"
                              scrollMonthYearDropdown
                              placeholderText="Select Till Date"
                              minDate={formData.from_date || null}
                              required
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </form>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={searchModalClose}
                disabled={formLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="subModuleEditForm"
                className="btn btn-primary"
                disabled={formLoading}
              >
                {formLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    {isMultipleMode ? 'Updating...' : 'Updating...'}
                  </>
                ) : (
                  isMultipleMode ? `Update Sub-Modules` : "Update Sub-Module"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
});

export default SubModuleUpdatePopup;